export const CLIENT_INTEGRATION_TYPES = [
  'NINJAONE',
  'MICROSOFT_365',
  'IT_GLUE',
  'THREECX',
  'SIP_PROVIDER',
  'OPENPROJECT',
] as const;

export type ClientIntegrationType = (typeof CLIENT_INTEGRATION_TYPES)[number];
export type ClientIntegrationStatus = 'CONNECTED' | 'NEEDS_ATTENTION' | 'DISCONNECTED';

export interface Client {
  id: string;
  name: string;
  slug: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ClientIntegration {
  id: string;
  clientId: string;
  type: ClientIntegrationType;
  externalId: string;
  displayName: string;
  status: ClientIntegrationStatus;
  metadata: Readonly<Record<string, never>>;
  connectedAt: string | null;
  lastVerifiedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ClientWithIntegrations extends Client {
  integrations: readonly ClientIntegration[];
}

export interface UnlinkedNinjaOrganization {
  organizationId: number;
  organizationName: string;
}

export interface ClientDirectory {
  clients: readonly ClientWithIntegrations[];
  unlinkedNinjaOrganizations: readonly UnlinkedNinjaOrganization[];
}

export interface CreateClientInput {
  name: string;
  slug?: string;
  active: boolean;
}

export type ClientErrorCode =
  | 'INVALID_CLIENT'
  | 'DUPLICATE_SLUG'
  | 'CLIENT_NOT_FOUND'
  | 'NINJA_ORG_NOT_FOUND'
  | 'NINJA_ORG_ALREADY_LINKED';

export class ClientValidationError extends Error {
  constructor(message: string, readonly code: ClientErrorCode) {
    super(message);
    this.name = 'ClientValidationError';
  }
}

export function slugFromName(name: string): string {
  return name.normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
    .slice(0, 56).replace(/-+$/g, '') || 'client';
}

export function validateClientInput(input: CreateClientInput): CreateClientInput {
  const name = typeof input.name === 'string' ? input.name.trim() : '';
  if (name.length === 0 || name.length > 160 || typeof input.active !== 'boolean') {
    throw new ClientValidationError('A valid client name and active state are required', 'INVALID_CLIENT');
  }
  const slug = input.slug === undefined ? undefined : input.slug.trim().toLowerCase();
  if (slug !== undefined && (slug.length > 64 || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug))) {
    throw new ClientValidationError('Client slug must use lowercase letters, numbers, and hyphens', 'INVALID_CLIENT');
  }
  return slug === undefined ? { name, active: input.active } : { name, slug, active: input.active };
}

export function validNinjaOrganizationId(value: unknown): number {
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value <= 0) {
    throw new ClientValidationError('A valid NinjaOne organization ID is required', 'INVALID_CLIENT');
  }
  return value;
}
